import { GridColDef } from "@mui/x-data-grid-pro";
import {
  DownloadIcon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "components";
import GKCharts from "components/GKCharts";
import dayjs from "dayjs";
import Highcharts from "highcharts/highstock";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import toast from "react-hot-toast";
import { useLocation, useParams } from "react-router-dom";
import { moneyCommaSeparator, numberToCurrency } from "utils/MathFunction";
import { NAV_LIST_BY_PORTFOLIO_ID } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { handleDowloadFile } from "utils/fileHelper";
import { extraColumnsField, matchDate } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import useDocumentTitle from "utils/useDocumentTitle";

const NAV = () => {
  useDocumentTitle("NAV");

  const { id } = useParams();
  const { state } = useLocation();

  const { data: navData, isFetching } = useFetchQuery({
    key: [QueryKeys.PORTFOLIO_NAV_LIST, id],
    route: NAV_LIST_BY_PORTFOLIO_ID(id),
  });

  const navValues = navData?.data?.map((item: any) => item.nav) || [];

  const calculateDrawdown = (values: number[]) => {
    if (values.length === 0) return [];

    let peak = values[0];
    return values.map((value) => {
      if (value > peak) {
        peak = value;
      }
      const drawdown = ((value - peak) / peak) * 100;
      return Number(drawdown.toFixed(2));
    });
  };

  const drawdownValues = calculateDrawdown(navValues);

  const drawdownData =
    navData?.data?.map((item: any, index: number) => [
      new Date(item?.date).getTime(),
      drawdownValues[index] || 0,
    ]) || [];

  const { mutate } = useMutation({
    mutationKey: ["EXPORT_NAV_DATA"],
    mutationFn: ({ id }: { id: any }) =>
      axiosInstance.get(`${NAV_LIST_BY_PORTFOLIO_ID(id)}export_csv/`),
    onSuccess: (response) => {
      handleDowloadFile({
        fileData: response.data,
        applicaionType: "text/csv",
        fileName: `NavList`,
        extensionType: "csv",
      });
      toast.success("Your file is successfully downloaded.");
    },
  });

  const NAVcolumns: GridColDef[] = [
    {
      ...extraColumnsField("Date", "date", 130, 0),
      valueFormatter: (value) => matchDate(value.value),
      renderCell: (item: any) => (
        <Typography>
          {dayjs(item?.row?.date).format("DD MMM YYYY") || "N/A"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("NAV", "nav", 60, 0),
      type: "number",
      renderCell: (item: any) => (
        <Typography>{moneyCommaSeparator(item?.row?.nav) || "-"}</Typography>
      ),
    },
    {
      ...extraColumnsField("Units", "units", 60, 0),
      type: "number",
      renderCell: (item: any) => (
        <Typography>{moneyCommaSeparator(item?.row?.units) || "-"}</Typography>
      ),
    },
    {
      ...extraColumnsField("Daily Ledger Balance", "cashBalance", 150, 0),
      type: "number",
      valueGetter: (item) => item?.row?.avlBal || "0",
      valueFormatter: (item) => moneyCommaSeparator(item?.value) || "0",
    },
    {
      ...extraColumnsField("Current Value", "currentValue", 130, 0),
      type: "number",
      renderCell: (item: any) => (
        <Typography>
          {moneyCommaSeparator(item?.row?.currentValue) || "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Portfolio Value", "portfolioValue", 130, 0),
      type: "number",
      renderCell: (item: any) => (
        <Typography>
          {moneyCommaSeparator(item?.row?.portfolioValue) || "-"}
        </Typography>
      ),
    },
  ];

  return (
    <DashboardLayout title="Portfolio NAV" loading={isFetching}>
      <Grid2 container spacing={3}>
        <Grid2 size={12}>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    style={{ padding: "16px" }}
                    width={"25%"}
                    sx={{
                      borderRight: "1px solid #E3E8EF",
                      p: 100,
                    }}
                  >
                    <Typography color={"var(--neutral-500)"} fontSize={12}>
                      Portfolio Name
                    </Typography>
                    <Typography fontWeight={600} fontSize={18}>
                      {state?.portfolioName}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ padding: "16px" }}
                    width={"25%"}
                    sx={{
                      borderRight: "1px solid #E3E8EF",
                    }}
                  >
                    <Typography color={"var(--neutral-500)"} fontSize={12}>
                      Portfolio Code
                    </Typography>
                    <Typography fontWeight={600} fontSize={18}>
                      {state?.portfolioCode}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{
                      padding: "16px",
                    }}
                    width={"25%"}
                    sx={{
                      borderRight: "1px solid #E3E8EF",
                    }}
                  >
                    <Typography color={"var(--neutral-500)"} fontSize={12}>
                      Ledger Balance
                    </Typography>
                    <Typography fontWeight={600} fontSize={18}>
                      {numberToCurrency(state?.ledgerBalance)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{
                      padding: "16px",
                    }}
                    width={"25%"}
                  >
                    <Typography color={"var(--neutral-500)"} fontSize={12}>
                      Last Cash Entry Date
                    </Typography>
                    <Typography fontWeight={600} fontSize={18}>
                      {matchDate(state?.lastCashDate)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid2>

        <Grid2 container size={12} spacing={2}>
          <Grid2
            size={12}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography fontSize={18} fontWeight={600}>
              Nav Chart
            </Typography>
            <GKButton
              style={{
                padding: "6px 10px",
                color: "var(--neutral-500)",
                border: "1px solid #E3E8EF",
                whiteSpace: "nowrap",
              }}
              startIcon={<DownloadIcon width={14} />}
              onClick={() => mutate({ id: id })}
            >
              Export NAVs
            </GKButton>
          </Grid2>
          <Grid2 size={12}>
            <GKCharts
              navigator
              chartOptions={{
                title: {
                  text: "",
                  align: "left",
                },
                chart: {
                  borderRadius: 5,
                  backgroundColor: "var(--white)",
                  height: 600,
                },
                tooltip: {
                  formatter(this: any) {
                    const formattedDate = Highcharts.dateFormat(
                      "%b %e, %Y",
                      this.x,
                    );

                    let content = `<b>${formattedDate}</b><br/>`;

                    this.points.forEach((point: any) => {
                      content += `<span style="color:${
                        point.color
                      }">\u25CF</span> ${
                        point.series.name
                      }: <b>${moneyCommaSeparator(point.y)}</b><br/>`;
                    });

                    return content;
                  },
                  pointFormatter(this: any) {
                    return `<span style="color:${this?.series?.color}">${
                      this?.series?.name
                    }</span>: ${moneyCommaSeparator(this?.y)}<br/>`;
                  },
                  valueDecimals: 2,
                  split: true,
                },
                plotOptions: {
                  series: {
                    states: { hover: { enabled: true } },
                    animation: true,
                    fillColor: "#F9EEED",
                  },
                },
                yAxis: [
                  {
                    height: "50%",
                    top: "0%",
                    offset: 0,
                    lineWidth: 1,
                    gridLineWidth: 1,
                    gridLineColor: "#E3E8EF",
                    labels: {
                      formatter(this: any) {
                        return moneyCommaSeparator(this.value);
                      },
                    },
                    min: 0,
                    max: null,
                    softMin: 0,
                    softMax: null,
                    startOnTick: true,
                    endOnTick: true,
                  },
                  {
                    top: "50%",
                    height: "50%",
                    offset: 0,
                    lineWidth: 1,
                    gridLineWidth: 0,
                    tickInterval: 20,
                    labels: {
                      formatter(this: any) {
                        return `${this.value}`;
                      },
                    },
                    min: null,
                    max: 0,
                    softMin: null,
                    softMax: 0,
                    startOnTick: true,
                    endOnTick: true,
                    plotLines: [
                      {
                        value: 0,
                        color: "var(--black)",
                        width: 1,
                        dashStyle: "Solid",
                        zIndex: 5,
                      },
                    ],

                    //   tickPositioner(this: any) {
                    //     const positions = [];

                    //     // Get the minimum drawdown value
                    //     const minDrawdown = Math.min(...drawdownValues);
                    //     const min = this.min || minDrawdown - 5;
                    //     const max = 0;

                    //     // Calculate range
                    //     const range = Math.abs(min);

                    //     console.log(range);

                    //     // Dynamic tick interval based on the range
                    //     let tickInterval;
                    //     if (range >= 100) {
                    //       tickInterval = 15; // Large range (100+)
                    //     } else if (range >= 50) {
                    //       tickInterval = 10; // Medium range (50-99)
                    //     } else if (range >= 20) {
                    //       tickInterval = 5; // Small range (20-49)
                    //     } else {
                    //       tickInterval = 2; // Very small range (<20)
                    //     }

                    //     console.log(tickInterval);

                    //     // Generate tick positions
                    //     for (let i = 0; i >= min; i -= tickInterval) {
                    //       console.log(i, "---");
                    //       Math.floor(positions.push(Number(i.toFixed(2))));
                    //     }

                    //     // Ensure we include the min value
                    //     if (positions[positions.length - 1] > min) {
                    //       console.log(min, "---min");
                    //       Math.ceil(positions.push(Number(min.toFixed(2))));
                    //     }

                    //     // Ensure 0 is always included
                    //     if (positions[0] !== 0) {
                    //       positions.unshift(0);
                    //     }

                    //     return positions.sort((a, b) => a - b);
                    //   },
                  },
                ],
                accessibility: { enabled: false },
                exporting: { enabled: false },
                credits: { text: "", enabled: false },
                legend: {
                  enabled: true,
                  layout: "horizontal",
                  align: "center",
                  verticalAlign: "top",
                  itemStyle: {
                    color: "var(--black)",
                    fontWeight: "bold",
                  },
                  symbolWidth: 20,
                  symbolHeight: 10,
                },
                xAxis: {
                  type: "date",
                  dateTimeLabelFormats: {
                    day: "%b %e, %Y",
                    week: "%b %e, %Y",
                    month: "%b %Y",
                    year: "%Y",
                  },
                  labels: {
                    style: {
                      color: "var(--primary-900)",
                      fontSize: "12px",
                      fontWeight: "bold",
                    },
                    formatter(this: any) {
                      return Highcharts.dateFormat("%b %e, %Y", this.value);
                    },
                  },
                },
                responsive: {
                  rules: [
                    {
                      condition: { maxWidth: 500 },
                      chartOptions: {
                        legend: {
                          layout: "horizontal",
                          align: "center",
                          verticalAlign: "bottom",
                        },
                      },
                    },
                  ],
                },
                rangeSelector: {
                  enabled: true,
                  inputStyle: {
                    color: "var(--primary-900)",
                    fontWeight: "normal",
                  },
                  labelStyle: {
                    color: "var(--primary-900)",
                    fontWeight: "normal",
                  },
                  buttonTheme: {
                    width: 50,
                    fill: `#ffffff`,
                    style: {
                      background: "var(--white)",
                      color: "var(--neutral-500)",
                      fontWeight: "normal",
                      textTransform: "uppercase",
                    },
                    states: {
                      select: {
                        fill: `#F8FAFC`,
                        style: {
                          fontWeight: "normal",
                          color: "var(--neutral-500)",
                        },
                      },
                    },
                  },
                },
                navigator: {
                  enabled: true,
                  height: 30,
                  xAxis: {
                    type: "datetime",
                    dateTimeLabelFormats: {
                      day: "%e of %b, %Y",
                    },
                  },
                },
                series: [
                  {
                    name: "NAV",
                    type: "line",
                    data: navData?.data?.map((item: any) => [
                      new Date(item.date).getTime(),
                      item.nav,
                    ]),
                    color: "var(--primary-900)",
                    yAxis: 0,
                  },
                  {
                    name: "Drawdown",
                    type: "area",
                    data: drawdownData,
                    color: "var(--error-color)",
                    fill: "var(--blue-400)",
                    fillOpacity: 0.5,
                    threshold: 0,
                    yAxis: 1,
                  },
                ],
              }}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12} container spacing={2}>
          <Grid2 size={12}>
            <Typography fontSize={16} fontWeight={600}>
              Portfolio Metrics
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <GKClientDataGrid
              name="navData"
              columns={NAVcolumns}
              rows={navData?.data || []}
              loading={isFetching}
              showHeaderGrid={false}
              maxHeight={`calc(100vh - 300px)`}
              density="compact"
            />
          </Grid2>
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default NAV;
